import { useEffect } from 'react';

import {
  resolveEmergencyCallDetailsStatus,
  resolveEmergencyConnectionPoint,
  shouldSyncEmergencyConnectionPointFromPrimaryHearing,
} from './useEmergencyCallDetailsUpdateFromPrimaryHearing.helpers';

import { useAppDispatch, useAppSelector } from 'features/app/hooks';
import { useBaseCallDetailsStatusUpdateFromParticipants } from 'features/call/call-details/hooks';
import type { CallDetailsStatusUpdateFromParticipantsData } from 'features/call/call-details/interfaces';
import { primaryHearingSelector } from 'features/call/call-hearing/store';
import { EmergencyConnectionPoint } from 'features/emergency/enums';
import {
  addCalledConnectionPoints,
  phoneNumberECRCSelector,
  phoneNumberPSAPSelector,
  setEmergencyCallDetailsStatus,
  setEmergencyConnectionPoint,
  setIsEmergencyOccurredInRegisteredLocation,
} from 'features/emergency/store';
import { selectIsAcceptingUserAndCurrentlyTeaming } from 'features/teaming/teaming-base/store';

const getCallDetailsStatus = ({
  hearing,
}: CallDetailsStatusUpdateFromParticipantsData) =>
  resolveEmergencyCallDetailsStatus({
    status: hearing.status,
    hasConnected: hearing.hasConnected,
  });

export const useEmergencyCallDetailsUpdateFromPrimaryHearing = () => {
  const dispatch = useAppDispatch();

  const primaryHearing = useAppSelector(primaryHearingSelector);
  const phoneNumberPSAP = useAppSelector(phoneNumberPSAPSelector);
  const phoneNumberECRC = useAppSelector(phoneNumberECRCSelector);
  const isAcceptingUserAndCurrentlyTeaming = useAppSelector(
    selectIsAcceptingUserAndCurrentlyTeaming
  );

  useBaseCallDetailsStatusUpdateFromParticipants(
    setEmergencyCallDetailsStatus,
    getCallDetailsStatus
  );

  useEffect(() => {
    if (
      !shouldSyncEmergencyConnectionPointFromPrimaryHearing({
        isAcceptingUserAndCurrentlyTeaming,
        primaryHearingStatus: primaryHearing.status,
      })
    ) {
      return;
    }

    const connectionPoint = resolveEmergencyConnectionPoint({
      primaryHearingPhoneNumber: primaryHearing.phoneNumber,
      phoneNumberPSAP,
      phoneNumberECRC,
    });

    dispatch(setEmergencyConnectionPoint(connectionPoint));
    dispatch(addCalledConnectionPoints(connectionPoint));

    if (connectionPoint === EmergencyConnectionPoint.OTHER) {
      return;
    }

    dispatch(
      setIsEmergencyOccurredInRegisteredLocation(
        connectionPoint === EmergencyConnectionPoint.PSAP
      )
    );
  }, [
    dispatch,
    isAcceptingUserAndCurrentlyTeaming,
    phoneNumberECRC,
    phoneNumberPSAP,
    primaryHearing.phoneNumber,
    primaryHearing.status,
  ]);
};
